import { useState } from "react";
import {
  Star,
  MessageCircle,
  Mail,
  MapPin,
  ShieldCheck,
  Factory,
  Truck,
  Store,
} from "lucide-react";

import { Card, CardContent, Button, Badge } from "@b2b/ui-components";
interface SupplierCardProps {
  supplier: {
    id: number;
    name: string;
    logo: string;
    country: string;
    countryFlag: string;
    yearsInBusiness: number;
    verified: boolean;
    rating: number;
    reviewCount: number;
    businessType: string;
    mainProducts: string[];
    productImages: string[];
    responseRate: string;
    responseTime: string;
  };
}

export const SupplierCard = ({ supplier }: SupplierCardProps) => {
  const [imageError, setImageError] = useState(false);

  const getCompanyInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };
  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-3 w-3 ${
              i < Math.floor(rating)
                ? "fill-orange-600 text-orange-500"
                : "text-gray-500"
            }`}
          />
        ))}
        <span className="text-xs text-gray-500 ml-1">
          ({supplier.reviewCount})
        </span>
      </div>
    );
  };

  const getBusinessTypeIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case "manufacturer":
        return <Factory className="h-3 w-3" />;
      case "exporter":
        return <Truck className="h-3 w-3" />;
      case "wholesaler":
        return <Store className="h-3 w-3" />;
      default:
        return <Factory className="h-3 w-3" />;
    }
  };

  return (
    <Card className="h-full hover:shadow-lg transition-all duration-300 border hover:border-orange-500">
      <CardContent className="p-6 h-full flex flex-col">
        {/* Header with Logo and Company Info */}
        <div className="flex gap-4 mb-4">
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 flex items-center justify-center">
            {!imageError ? (
              <img
                src={supplier.logo}
                alt={`${supplier.name} logo`}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="w-full h-full bg-orange-100 flex items-center justify-center">
                <span className="text-orange-500 font-bold text-lg">
                  {getCompanyInitials(supplier.name)}
                </span>
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg text-foreground line-clamp-2 mb-1">
              {supplier.name}
            </h3>

            <div className="flex items-center gap-2 mb-2">
              {supplier.verified && (
                <Badge
                  variant="outline"
                  className="text-xs text-green-700 border-green-500 bg-green-100"
                >
                  <ShieldCheck className="h-3 w-3 mr-1" />
                  Verified
                </Badge>
              )}
              <span className="text-xs text-gray-500">
                {supplier.yearsInBusiness} years in business
              </span>
              <span className="text-xs flex items-center gap-1">
                <span>{supplier.countryFlag}</span>
                <span className="text-gray-500">{supplier.country}</span>
              </span>
            </div>

            {/* {renderStars(supplier.rating)} */}
          </div>
        </div>

        {/* Product Preview Strip */}
        <div className="mb-4">
          <div className="grid grid-cols-4 gap-2">
            {supplier.productImages.slice(0, 4).map((image, index) => (
              <div
                key={index}
                className="aspect-square rounded-md overflow-hidden bg-gray-100"
              >
                <img
                  src={image}
                  alt={`Product ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Key Information */}
        <div className="space-y-3 mb-4 flex-1">
          <div className="flex items-center gap-2 text-sm">
            {getBusinessTypeIcon(supplier.businessType)}
            <span className="font-medium text-foreground">
              {supplier.businessType}
            </span>
          </div>

          <div>
            <p className="text-sm text-gray-500 mb-1">Main Products:</p>
            <p className="text-sm text-foreground line-clamp-2">
              {supplier.mainProducts.join(", ")}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
            <div>
              <span className="block">Response Rate:</span>
              <span className="font-medium text-foreground">
                {supplier.responseRate}
              </span>
            </div>
            <div>
              <span className="block">Response Time:</span>
              <span className="font-medium text-foreground">
                {supplier.responseTime}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2 pt-4 border-t border-border">
          <Button className="w-full" size="sm" variant="primary">
            <Mail className="h-4 w-4 mr-2" />
            Contact Supplier
          </Button>
          <Button variant="outline" className="w-full" size="sm">
            <MessageCircle className="h-4 w-4 mr-2" />
            Chat Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SupplierCard;
